import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useLocation, Link } from 'react-router-dom';
import { Sun, Moon } from 'lucide-react';
import LanguageSelector from './LanguageSelector';
import { getStoreUrl } from '../utils/storeUrls';
import { isNavHidden } from '../utils/navVisibility';
import { useTheme } from '../hooks/useTheme';

interface WaveLogoProps {
  size?: number;
  color?: string;
  backgroundColor?: string;
  onClick?: (e: React.MouseEvent) => void;
}

const WaveLogo = ({ 
  size = 40, 
  color = '#4F46E5', 
  backgroundColor = 'transparent',
  onClick 
}: WaveLogoProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<number | null>(null);
  const [progress, setProgress] = useState(0);
  const [isAnimating, setIsAnimating] = useState(true);

  useEffect(() => {
    if (!isAnimating) {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      return;
    }

    const animate = () => {
      setProgress(prev => (prev + 0.005) % 1);
      animationRef.current = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [isAnimating]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    const dpr = window.devicePixelRatio || 1;
    
    canvas.width = size * dpr;
    canvas.height = size * dpr;
    canvas.style.width = `${size}px`;
    canvas.style.height = `${size}px`;
    ctx.scale(dpr, dpr);

    ctx.clearRect(0, 0, size, size);

    // Draw background
    if (backgroundColor !== 'transparent') {
      ctx.fillStyle = backgroundColor;
      ctx.fillRect(0, 0, size, size);
    }

    const center = size / 2;
    const barWidth = size * 0.12;
    const maxHeight = center * 1.6;
    const lineWidth = size * 0.08;

    ctx.strokeStyle = color;
    ctx.lineWidth = lineWidth;
    ctx.lineCap = 'round';

    // Draw 5 bars
    for (let i = -2; i <= 2; i++) {
      let heightFactor;
      
      if (!isAnimating) {
        // Static heights when animation is stopped
        const heightFactors = [0.5, 0.75, 1.0, 0.75, 0.5];
        heightFactor = heightFactors[i + 2];
      } else {
        // Animated wave effect
        const phaseOffset = (i + 2) * 0.25;
        const baseHeight = 1.0 - (0.2 * Math.abs(i));
        heightFactor = baseHeight * (0.5 + 0.5 * Math.sin(progress * Math.PI * 2 + phaseOffset));
      }

      const height = maxHeight * heightFactor;
      const x = center + i * barWidth * 1.3;

      ctx.beginPath();
      ctx.moveTo(x, center - height / 2);
      ctx.lineTo(x, center + height / 2);
      ctx.stroke();
    }
  }, [size, color, backgroundColor, progress, isAnimating]);

  const handleClick = (e: React.MouseEvent) => {
    setIsAnimating(!isAnimating);
    if (onClick) {
      onClick(e);
    }
  };

  return (
    <canvas
      ref={canvasRef}
      onClick={handleClick}
      style={{ cursor: 'pointer' }}
    />
  );
};

// Navigation Component with WaveLogo
const Navigation = () => {
  const { t, i18n } = useTranslation();
  const { locale } = useParams<{ locale?: string }>();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [navOpacity, setNavOpacity] = useState(0);
  const { theme, toggleTheme } = useTheme();

  useEffect(() => {
    const handleScroll = () => {
      setNavOpacity(Math.min(window.scrollY / 80, 1));
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  // Get locale from URL path as fallback if useParams doesn't work
  const getLocaleFromPath = () => {
    const pathSegments = location.pathname.split('/').filter(Boolean);
    const supportedLocales = ['en', 'es', 'fr', 'de', 'pt', 'vi', 'sv'];
    if (pathSegments.length > 0 && supportedLocales.includes(pathSegments[0])) {
      return pathSegments[0];
    }
    return i18n.language || 'en';
  };
  
  const currentLocale = locale || getLocaleFromPath();
  
  const isActive = (path: string) => {
    const expectedPath = `/${currentLocale}${path}`;
    return location.pathname === expectedPath || 
           (path === '/' && (location.pathname === `/${currentLocale}` || location.pathname === `/${currentLocale}/`));
  };

  const isHomePage = isActive('/');
  const isTransparent = isHomePage && navOpacity < 0.5;
  const isCampaignPage = location.pathname.startsWith('/c/');

  const handleFAQClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (isHomePage) {
      e.preventDefault();
      document.getElementById('faq')?.scrollIntoView({ behavior: 'smooth' });
    }
    // If not on homepage, let browser navigate to /{locale}/#faq (full nav with hash scroll)
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };


  const handleLogoClick = (_e: React.MouseEvent) => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' as ScrollBehavior });
  };

  // Placed after every hook so the hook order stays stable across routes.
  if (isNavHidden(location.pathname)) return null;

  return (
    <header
      className={`${isCampaignPage ? 'flex justify-center' : 'grid grid-cols-3'} items-center p-4 fixed top-0 left-0 right-0 z-50`}
      style={{ background: 'transparent', border: 'none' }}
    >
      {/* Scroll-fade backdrop */}
      <div
        aria-hidden="true"
        style={{
          position: 'absolute', inset: 0, zIndex: -1,
          background: 'var(--v2-nav-bg)',
          backdropFilter: 'blur(14px)',
          WebkitBackdropFilter: 'blur(14px)',
          borderBottom: '1px solid var(--v2-line)',
          opacity: navOpacity,
          transition: 'opacity 0.4s ease',
          pointerEvents: 'none',
        }}
      />
      <div className="flex items-center">
        <Link
          to={`/${currentLocale}/`}
          className="flex items-center space-x-2"
          onClick={handleLogoClick}
        >
          <WaveLogo
            size={24}
            color="#4F46E5"
          />
          <span className={`font-semibold whitespace-nowrap transition-colors duration-300 ${isTransparent ? 'text-white' : 'text-gray-900 dark:text-white'}`}>Notely AI</span>
        </Link>
      </div>

      {!isCampaignPage && (
        <nav className="hidden md:flex justify-center items-center space-x-8">
          <Link
            to={`/${currentLocale}/features`}
            className={`text-sm transition-colors duration-300 ${isActive('/features') ? 'text-primary' : isTransparent ? 'text-white/85 hover:text-white' : 'text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white'}`}
          >
            {t('nav.features')}
          </Link>
          <Link
            to={`/${currentLocale}/pricing`}
            className={`text-sm transition-colors duration-300 ${isActive('/pricing') ? 'text-primary' : isTransparent ? 'text-white/85 hover:text-white' : 'text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white'}`}
          >
            {t('nav.pricing')}
          </Link>
          <a
            href={`/${currentLocale}/#faq`}
            onClick={handleFAQClick}
            className={`text-sm whitespace-nowrap transition-colors duration-300 ${isTransparent ? 'text-white/85 hover:text-white' : 'text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white'}`}
          >
            {t('nav.faq', 'FAQ')}
          </a>
          <Link
            to={`/${currentLocale}/contact`}
            className={`text-sm transition-colors duration-300 ${isActive('/contact') ? 'text-primary' : isTransparent ? 'text-white/85 hover:text-white' : 'text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white'}`}
          >
            {t('nav.contact')}
          </Link>
        </nav>
      )}

      {!isCampaignPage && (
        <div className="col-start-3 flex items-center justify-end space-x-4">
          <div className="hidden md:flex items-center space-x-2">
            <button onClick={toggleTheme} className={`p-2 rounded-full transition-colors duration-300 ${isTransparent ? 'text-white/85 hover:text-white hover:bg-white/10' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-white dark:hover:bg-slate-700'}`}>
              {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </button>
            <LanguageSelector isTransparent={isTransparent} />
          </div>
          {(() => {
            const storeUrl = getStoreUrl(i18n.language);
            return (
              <a
                href={storeUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex bg-primary text-white px-5 py-2 rounded-full text-sm font-medium hover:bg-primary/90 whitespace-nowrap"
              >
                {t('nav.download')}
              </a>
            );
          })()}
          <button
            onClick={toggleMobileMenu}
            className={`md:hidden transition-colors duration-300 ${isTransparent ? 'text-white/85 hover:text-white' : 'text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white'}`}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d={isMobileMenuOpen ? "M6 18L18 6M6 6l12 12" : "M4 6h16M4 12h16M4 18h16"}
              />
            </svg>
          </button>
        </div>
      )}

      {/* Mobile menu */}
      {!isCampaignPage && isMobileMenuOpen && (
        <div
          className="absolute top-full left-0 right-0 shadow-lg p-4 md:hidden"
          style={{
            background: 'var(--v2-nav-bg)',
            backdropFilter: 'blur(14px)',
            WebkitBackdropFilter: 'blur(14px)',
            borderBottom: '1px solid var(--v2-line)',
            opacity: Math.max(navOpacity, 0.92),
          }}
        >
          <nav className="flex flex-col space-y-4">
            <Link
              to={`/${currentLocale}/features`}
              className={`text-sm transition-colors duration-300 ${isActive('/features') ? 'text-primary' : 'text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white'}`}
              onClick={() => setIsMobileMenuOpen(false)}
            >
              {t('nav.features')}
            </Link>
            <Link
              to={`/${currentLocale}/pricing`}
              className={`text-sm transition-colors duration-300 ${isActive('/pricing') ? 'text-primary' : 'text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white'}`}
              onClick={() => setIsMobileMenuOpen(false)}
            >
              {t('nav.pricing')}
            </Link>
            <a
              href={`/${currentLocale}/#faq`}
              className="text-sm transition-colors duration-300 text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white"
              onClick={(e) => { handleFAQClick(e); setIsMobileMenuOpen(false); }}
            >
              {t('nav.faq', 'FAQ')}
            </a>
            <Link
              to={`/${currentLocale}/contact`}
              className={`text-sm transition-colors duration-300 ${isActive('/contact') ? 'text-primary' : 'text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white'}`}
              onClick={() => setIsMobileMenuOpen(false)}
            >
              {t('nav.contact')}
            </Link>
          </nav>
          <div className="border-t border-gray-200 dark:border-slate-700 pt-4 mt-2 flex items-center justify-between">
            <button onClick={toggleTheme} className="p-2 rounded-full text-gray-600 hover:text-gray-900 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-white dark:hover:bg-slate-700 transition-colors">
              {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </button>
            <LanguageSelector />
          </div>
        </div>
      )}
    </header>
  );
};

export default Navigation;